// JobDialog.js
// Job creation/editing dialog component

import React, { useState, useEffect, useCallback, useRef } from 'react'; // Added useMemo, useRef
// Added axios
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Paper,
  Typography,
  Box,
  CircularProgress // Added Alert
} from '@mui/material';


// Removed GoogleMapReact import
// Import Leaflet
import 'leaflet/dist/leaflet.css'; // Import Leaflet CSS
// Import Leaflet utils

import {
  defaultJobData
} from '../../constants/dashboardConstants';
import {
  handleInputChange as handleInputChangeUtil,
  handleNestedInputChange as handleNestedInputChangeUtil,
  handleNotesInputChange,
  copyCustomerToPickup,
  validateJobData,
  formatJobDataForSubmission,
  formatJobDataForUpdate,
  parseJobForEditing,
  generateJobId
} from '../../utils/dashboardUtils';
import useLocationRequest from '../../hooks/useLocationRequest';
import useAvailableDrivers from '../../hooks/useAvailableDrivers';
import { useUser } from '../../context/UserContext'; // Import useUser hook
// Import the new service function
import { createJob, updateJob, requestCustomerLocation } from '../../services/jobService'; // Added geocodeAddressAPI

// Import sub-components
import CustomerSection from './CustomerSection';
import VehicleSection from './VehicleSection';
import ServiceSection from './ServiceSection';
import NotesSection from './NotesSection';
import JobMediaSection from './JobMediaSection';
import LocationSection from './LocationSection';
import ContactSection from './ContactSection';
// Import the reusable map component from its own file
import { JobMapComponent } from '../JobMapComponent';

// Removed GoogleMapReact specific MarkerComponent


/**
 * Job Dialog component for creating and editing jobs
 */
const JobDialog = ({
  open,
  onClose,
  isEditing,
  selectedJob,
  userProfile: propUserProfile,
  userCompanies: propUserCompanies,
  vehicleData,
  onJobCreated,
  onJobUpdated,
  setNotification
}) => {
  // Get user profile data directly from the context
  const { currentUser: userProfile, isLoading: userProfileLoading } = useUser(); // Use useUser hook, alias currentUser

  // Use props or hook data (prefer hook data if available)
  // Note: userCompanies was part of useUserProfile, need to decide if it's needed and where to get it from (e.g., currentUser)
  const effectiveUserProfile = userProfile || propUserProfile;
  const effectiveUserCompanies = propUserCompanies || []; // Use propUserCompanies, default to empty array

  // Driver-only users (including temporary providers) get a view-only dialog:
  // photos/signatures work, but they cannot create or update job details.
  const dialogUserRoles = effectiveUserProfile?.secondaryRoles || {};
  const dialogActiveRoles = Array.isArray(dialogUserRoles)
    ? dialogUserRoles
    : Object.keys(dialogUserRoles).filter(r => dialogUserRoles[r] === true);
  const isViewOnlyUser = effectiveUserProfile?.isTemporaryProvider === true ||
    (dialogActiveRoles.length === 1 && dialogActiveRoles.includes('driver') && !['SP', 'OW', 'sOW', 'OWp', 'RM'].includes(effectiveUserProfile?.primaryRole));

  // State for job data
  const [newJobData, setNewJobData] = useState({ ...defaultJobData, account: '' });
  // State for auto-assign preference (default to true for new jobs, false when editing)
  const [autoAssignPreference, setAutoAssignPreference] = useState(!isEditing);

  // State for job ID
  const [jobId, setJobId] = useState(generateJobId());

  // State for active tab in notes section
  const [activeNotesTab, setActiveNotesTab] = useState('internal');

  // State for loading
  const [isLoading, setIsLoading] = useState(false);

  // State for fleet vehicles
  const [fleetVehicles, setFleetVehicles] = useState([]);

  // Map State (Only Dropoff state remains here, Service map uses JobMapComponent)
  const dropoffGeocodeTimerRef = useRef(null); // Ref for dropoff location debounce timer
  // Removed service map state/refs
  // State for Dropoff Location Map (Keep for now, might be handled by LocationSection or another JobMapComponent)
  const [dropoffMapCenter, setDropoffMapCenter] = useState({ lat: 39.8283, lng: -98.5795 }); // Default US center
  const [dropoffMapZoom, setDropoffMapZoom] = useState(4);


  // Use location request hook
  const {
    locationRequestSent,
    isLoading: locationLoading, // Renamed to avoid conflict with main isLoading
    locationData,
    // sendLocationRequest, // This function from the hook might not be needed anymore
    resetLocationRequest,
    parseLocationAddress
  } = useLocationRequest();

  // *** Pass jobZip to useAvailableDrivers hook with skipDistanceFilter=true for Create/Edit dialog ***
  const jobZipForDriverFilter = newJobData?.serviceLocation?.zip;
  const {
    availableDrivers,
    isLoading: driversLoading,
    error: driversError
  } = useAvailableDrivers(open, jobZipForDriverFilter, true); // Pass jobZip with skipDistanceFilter=true

  // Helper function to get auth headers
  const getAuthHeader = useCallback(() => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  // Removed Google Maps API Key fetching useEffect


  // Fetch fleet vehicles when dialog opens
  useEffect(() => {
    if (open) {
      // First check localStorage for cached vehicles
      const cachedVehicles = localStorage.getItem('fleetVehicles');
      if (cachedVehicles) {
        try {
          const parsedVehicles = JSON.parse(cachedVehicles);
          // console.log('JobDialog - Using cached fleet vehicles from localStorage:', parsedVehicles.length);
          setFleetVehicles(parsedVehicles);
        } catch (e) {
          console.error('Error parsing cached vehicles:', e);
          // Continue with API fetch if parse fails
          fetchFromAPI();
        }
      } else {
        // If not in localStorage, fetch from API
        fetchFromAPI();
      }
    }

    async function fetchFromAPI() {
      try {
        // Try to get vehicles from settings API
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/settings`);
        const data = await response.json();

        if (data && data.vehicles && Array.isArray(data.vehicles)) {
          // console.log('JobDialog - Fetched fleet vehicles from API:', data.vehicles.length);
          // console.log('JobDialog - Fleet vehicles data:', data.vehicles);
          setFleetVehicles(data.vehicles);

          // Cache in localStorage for other components
          localStorage.setItem('fleetVehicles', JSON.stringify(data.vehicles));
        } else {
          // console.log('JobDialog - No fleet vehicles found in API response');
          setFleetVehicles([]);
        }
      } catch (error) {
        console.error('Error fetching fleet vehicles:', error);
        // If error occurs, set empty array
        setFleetVehicles([]);
      }
    }
  }, [open]);

  // Debug: Log fleet vehicles state
  // useEffect(() => {
    // console.log('JobDialog - Current fleetVehicles state:', fleetVehicles);
  // }, [fleetVehicles]); // Commented out log

  // Set initial job data when editing or opening for new job
  useEffect(() => {
    if (open) {
      let initialData;
      if (isEditing && selectedJob) {
        initialData = parseJobForEditing(selectedJob);
        setJobId(selectedJob.po || selectedJob.id);
        // Set map centers based on existing job locations
        if (initialData.serviceLocation?.lat && initialData.serviceLocation?.lng) {
          // Removed setServiceMapCenter and setServiceMapZoom calls
        } else {
          // Removed setServiceMapCenter and setServiceMapZoom calls
        }
        if (initialData.dropoffLocation?.lat && initialData.dropoffLocation?.lng) {
          setDropoffMapCenter({ lat: initialData.dropoffLocation.lat, lng: initialData.dropoffLocation.lng });
          setDropoffMapZoom(14);
        } else {
          // Default dropoff map center to service center if available, else US center
          setDropoffMapCenter(initialData.serviceLocation?.lat ? { lat: initialData.serviceLocation.lat, lng: initialData.serviceLocation.lng } : { lat: 39.8283, lng: -98.5795 });
          setDropoffMapZoom(initialData.serviceLocation?.lat ? 14 : 4);
        }
      } else {
        initialData = { ...defaultJobData, account: '' };
        setJobId(generateJobId());
        // Default both maps to US center for new jobs
        // Removed setServiceMapCenter and setServiceMapZoom calls
        setDropoffMapCenter({ lat: 39.8283, lng: -98.5795 });
        setDropoffMapZoom(4);
      }

      // Set account after user profile is potentially loaded
      if (!userProfileLoading && effectiveUserProfile) {
        // Use the company name from the user profile, or default to empty string
        initialData.account = effectiveUserProfile.companyName || '';
      }
      setNewJobData(initialData);
      // Reset auto-assign preference when dialog opens/mode changes
      setAutoAssignPreference(!isEditing);

    }
  }, [open, isEditing, selectedJob, effectiveUserProfile, userProfileLoading]);


  // Update service location when location data is received from hook
  useEffect(() => {
    if (locationData) {
      const parsedAddress = parseLocationAddress(locationData);
      if (parsedAddress) {
        setNewJobData(prev => ({
          ...prev,
          serviceLocation: parsedAddress
        }));
        // Update service map center when location is received
        if (parsedAddress.lat && parsedAddress.lng) {
            // Removed setServiceMapCenter and setServiceMapZoom calls
        }

        setNotification({
          open: true,
          message: 'Customer location received',
          severity: 'success'
        });
      }
    }
  }, [locationData, parseLocationAddress, setNotification]);

  // Reset location request when dialog closes
  useEffect(() => {
    if (!open) {
      resetLocationRequest();
    }
  }, [open, resetLocationRequest]);

  // Removed the old backend geocoding useEffect hook.
  // Geocoding is now handled within JobMapComponent.

  // Handler for input change
  const handleInputChange = (field, value) => {
    setNewJobData(prevData => handleInputChangeUtil(prevData, field, value));
  };

  // Handler for nested input change
  const handleNestedInputChange = (parent, field, value) => {
    setNewJobData(prevData => handleNestedInputChangeUtil(prevData, parent, field, value));
  };

  // Handler for notes tab change
  const handleNotesTabChange = (tab) => {
    setActiveNotesTab(tab);
  };

  // Handler for copying customer info to pickup contact
  const handleCopyCustomer = () => {
    setNewJobData(prevData => copyCustomerToPickup(prevData));
  };

  // Handler for requesting customer location - Updated
  const handleRequestLocation = async () => {
    const currentJobId = isEditing ? selectedJob?.id : null; // Need job ID if editing
    if (!currentJobId && !isEditing) {
        // For new jobs, we might need to save a draft first or handle differently
        // For now, let's assume it only works for existing jobs
         setNotification({ open: true, message: 'Save the job first before requesting location.', severity: 'warning' });
         return;
    }
     if (!currentJobId && isEditing) {
         setNotification({ open: true, message: 'Cannot request location: Job ID is missing.', severity: 'error' });
         return;
     }

    // Enhanced check for customer phone number
    const customerPhone = newJobData.customerPhone?.trim(); // Trim whitespace
    if (!customerPhone) {
      setNotification({
        open: true,
        message: 'Customer phone number is required to request location',
        severity: 'error'
      });
      return;
    }

    // Call the service function which hits the backend endpoint
    // Pass jobId and the trimmed customerPhone
    const result = await requestCustomerLocation(currentJobId, customerPhone);

    if (result.success) {
      setNotification({
        open: true,
        message: 'Location request SMS sent to customer',
        severity: 'success'
      });
      // Optionally update UI state if needed, e.g., locationRequestSent from hook might be redundant now
    } else {
       setNotification({
        open: true,
        message: result.message || 'Failed to send location request',
        severity: 'error'
      });
    }
  };

  // Removed handleMapClick for the service location map as JobMapComponent handles its own interactions

  // Handler for dropoff map click
  const handleDropoffMapClick = ({ lat, lng }) => {
    // console.log("Dropoff Map clicked:", { lat, lng });
    // Update dropoff location coordinates
    setNewJobData(prev => ({
      ...prev,
      dropoffLocation: {
        ...(prev.dropoffLocation || {}),
        lat: lat,
        lng: lng
      }
    }));
    // Update dropoff map center
    setDropoffMapCenter({ lat, lng });
    // Optionally perform reverse geocoding here
  };


  // Handler for job creation
  const handleJobCreate = async () => {
    // Validate job data
    const validation = validateJobData(newJobData);

    if (!validation.isValid) {
      setNotification({
        open: true,
        message: `Please fill in all required fields: ${validation.missingFields.join(', ')}`,
        severity: 'error'
      });
      return;
    }

    // Format job data for submission, including auto-assign preference
    const jobDataToSubmit = {
      ...formatJobDataForSubmission(newJobData, availableDrivers),
      autoAssignPreference: autoAssignPreference // Add the preference flag
    };

    // console.log('Submitting job data (with auto-assign pref):', jobDataToSubmit);

    setIsLoading(true);

    try {
      // Make API call to create job
      const result = await createJob(jobDataToSubmit);

      if (result.success) {
        // Show success notification
        setNotification({
          open: true,
          message: 'Job created successfully',
          severity: 'success'
        });

        // Close dialog and reset form
        onClose();

        // Trigger refresh of job list
        if (onJobCreated) {
          onJobCreated();
        }
      } else {
        // Show error notification
        setNotification({
          open: true,
          message: result.message,
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error creating job:', error);

      // Show error notification
      setNotification({
        open: true,
        message: 'Failed to create job',
        severity: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handler for job update
  const handleJobUpdate = async () => {
    if (!selectedJob) {
      setNotification({
        open: true,
        message: 'No job selected for update',
        severity: 'error'
      });
      return;
    }

    // Debug log for notes before validation
    // console.log('JobDialog - Notes before update:');
    // console.log('- internalNotes:', newJobData.internalNotes);
    // console.log('- dispatcherNotes:', newJobData.dispatcherNotes);
    // console.log('- invoiceNotes:', newJobData.invoiceNotes);

    // Validate job data
    const validation = validateJobData(newJobData);

    if (!validation.isValid) {
      setNotification({
        open: true,
        message: `Please fill in all required fields: ${validation.missingFields.join(', ')}`,
        severity: 'error'
      });
      return;
    }

    // Format job data for update
    const jobDataToSubmit = formatJobDataForUpdate(newJobData, selectedJob, availableDrivers);

    // console.log('Updating job data:', jobDataToSubmit);

    setIsLoading(true);

    try {
      // Make API call to update job
      const result = await updateJob(selectedJob.id, jobDataToSubmit);

      if (result.success) {
        // Show success notification
        setNotification({
          open: true,
          message: result.message,
          severity: 'success'
        });

        // Close dialog and reset form
        onClose();

        // Trigger refresh of job list
        if (onJobUpdated) {
          onJobUpdated();
        }
      } else {
        // Show error notification
        setNotification({
          open: true,
          message: result.message,
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error updating job:', error);

      // Show error notification
      setNotification({
        open: true,
        message: 'Failed to update job',
        severity: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate filteredDrivers using useMemo unconditionally
  // This is now handled internally by ServiceSection using the hook
  // const filteredAvailableDrivers = useMemo(() => { ... }, [availableDrivers, effectiveUserProfile]);

  return (
    <Dialog
      open={open}
      onClose={isLoading ? undefined : onClose}
      disableEscapeKeyDown={isLoading}
      maxWidth="lg" // Changed to lg for more space
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{isEditing ? 'Edit Job' : 'Create Job'}</Typography>
          <Typography variant="h6">{jobId}</Typography>
        </Box>
        <Typography variant="caption" color="textSecondary">
          Fields marked with * are required
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          {/* Left Column (Forms) */}
          <Grid item xs={12} md={7}> {/* Adjusted grid size */}
            {!userProfileLoading ? (
            <CustomerSection
              jobData={newJobData}
              handleInputChange={handleInputChange}
              currentUser={effectiveUserProfile}
            />
            ) : (
              <Paper sx={{ p: 2, mb: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '150px' }}>
                <CircularProgress />
              </Paper>
            )}

            <VehicleSection
              jobData={newJobData}
              handleInputChange={handleInputChange}
              vehicleData={vehicleData}
              fleetVehicles={fleetVehicles}
            />

            <ServiceSection
              jobData={newJobData}
              handleInputChange={handleInputChange}
              // Pass the drivers fetched by the hook in *this* component
              availableDrivers={availableDrivers}
              vehicles={fleetVehicles}
              currentUser={effectiveUserProfile}
              autoAssignPreference={autoAssignPreference} // Pass state down
              handleAutoAssignChange={(e) => setAutoAssignPreference(e.target.checked)} // Pass handler down
            />

            <NotesSection
              jobData={newJobData}
              handleInputChange={handleInputChange}
              handleNotesInputChange={(noteType, value) => setNewJobData(prevData => handleNotesInputChange(prevData, noteType, value))}
              activeTab={activeNotesTab}
              handleTabChange={handleNotesTabChange}
              userProfile={effectiveUserProfile}
            />

            {/* Photos & signatures (existing jobs only). Available to everyone
                who can open the dialog, including driver-only users. */}
            {isEditing && (newJobData.id || newJobData._id) && (
              <JobMediaSection jobId={newJobData.id || newJobData._id} job={newJobData} />
            )}

          </Grid>

          {/* Right Column (Map and Locations) */}
          <Grid item xs={12} md={5}> {/* Adjusted grid size */}

             {/* Use JobMapComponent for Service Location */}
             <Typography variant="subtitle1" gutterBottom sx={{ mt: 1 }}>Service Location Map</Typography>
             <Box mb={2} style={{ height: '300px', border: '1px solid #ccc' }}>
               <JobMapComponent locationData={newJobData.serviceLocation} />
             </Box>
             {/* Removed old service map effects and refs */}

            <LocationSection
              jobData={newJobData}
              handleInputChange={handleInputChange}
              handleNestedInputChange={handleNestedInputChange}
              locationRequestSent={locationRequestSent}
              locationLoading={locationLoading}
              handleRequestLocation={handleRequestLocation}
              // Removed map props passed to LocationSection as it now handles its own Leaflet map
              setNewJobData={setNewJobData} // Pass state setter down (still needed for map click updates)
              // handleDropoffMapClick is no longer needed here as LocationSection handles its clicks internally
            />

            <ContactSection
              jobData={newJobData}
              handleNestedInputChange={handleNestedInputChange}
              handleCopyCustomer={handleCopyCustomer}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={isLoading}
          sx={{ mr: 1 }}
        >
          Cancel
        </Button>
        {!isViewOnlyUser && (
          <Button
            variant="outlined"
            color="primary"
            disabled={isLoading}
            sx={{ mr: 1 }}
          >
            SAVE DRAFT
          </Button>
        )}
        {!isViewOnlyUser && (
          <Button
            variant="contained"
            color="primary"
            onClick={isEditing ? handleJobUpdate : handleJobCreate}
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={20} /> : null}
          >
            {isLoading ? (isEditing ? 'Updating...' : 'Creating...') : (isEditing ? 'UPDATE JOB' : 'CREATE JOB')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default JobDialog;
